import React, { useState, useRef } from 'react';
import { ThemeSwiper, IThemeSwiperRef, IThemeSwiperProps } from '@base/lib/ThemeSwiper';
import { getModuleCode, getModuleElementId, getWrapStyle, getFeatureBannerTrackData } from 'base/utils/features';
import BannerImage, { IImageShowMode } from '@base/features/BannerImage';
import PopupDrawerModal from 'base/components/common/PopupDrawerModal';
import type { IBanner, IFeatureBanner, IPopupsCode } from 'mod/feature/interface';
import styles from './index.module.less';
import { isVideoPlaying } from 'base/utils/media';
import { TTrackElementAction, trackElement } from 'base/utils/tracker';
import cn from 'classnames';

export declare type ISwiperBannerProps = IThemeSwiperProps & {
    imgShowMode?: IImageShowMode;
    data: IFeatureBanner;
}

const isPhone = process.env.NEXT_PUBLIC_TERMINAL === 'm';

const SwiperBanner: React.FC<ISwiperBannerProps> = props => {
    const { imgShowMode = 'image', data, ...swiperProps } = props;
    const swiperRef = useRef<IThemeSwiperRef>(null);

    const list = data.lists;
    const height = data.config?.height
        ? (isPhone ? (data.config?.height / 2) : data.config?.height)
        : 700;
    const width = data.config?.width
        ? (isPhone ? (data.config?.width / 2) : data.config?.width)
        : (isPhone ? 375 : 1920);
    const moduleElementId = getModuleElementId(data);
    const moduleCode = getModuleCode(data);

    const [popupProps, setPopupProps] = useState<{ popUpsCode?: IPopupsCode; }>();

    const [popupVisible, setPopupVisible] = useState<boolean>(false)

    const onPopupClickHandle = (banner: IBanner) => {
        setPopupProps({
            popUpsCode: banner.popUps
        })
        setPopupVisible(true)
    }

    const handleAutoplay = (autoplay: boolean) => {
        if (!data.config?.carouselFlag) {
            return;
        }
        if (autoplay) {
            swiperRef.current?.startAutoplay();
        } else {
            swiperRef.current?.stopAutoplay();
        }
    };

    const trackSlide = (action: TTrackElementAction, index: number) => {
        trackElement({
            action,
            ...getFeatureBannerTrackData(data, index)
        });
    };

    return (
        <>
            <ThemeSwiper
                ref={swiperRef}
                id={moduleElementId}
                data-t-mod={moduleCode}
                className={cn(styles['swiper-banner'], { 'site-space': data.config?.hasSideSpace && isPhone })}
                style={getWrapStyle(data)}
                loop
                pagination
                navigation={!isPhone}
                effect={isPhone ? void 0 : 'fade'}
                autoplay={!data.config?.carouselFlag ? false : {
                    delay: 5000,
                    disableOnInteraction: false,
                }}
                list={list}
                renderSlideProps={
                    !moduleCode ?
                        void 0
                        : (item: IBanner, index) => ({
                            'data-t-tar': item.linkUrl || item.picUrl,
                            'data-t-pos': index + 1,
                            onClick: () => {
                                trackSlide('click', index);
                            }
                        })
                }
                render={(item: IBanner, index) => (
                    <BannerImage
                        onPopupClick={onPopupClickHandle}
                        index={index}
                        mode={imgShowMode}
                        banner={item}
                        width={width}
                        height={height}
                        videoProps={{
                            onPlay: () => handleAutoplay(false),
                            onPause: () => handleAutoplay(true),
                            onEnd: () => handleAutoplay(true),
                        }}
                    />
                )}
                onInit={() => trackSlide('impr', 0)}
                onSlideChangeTransitionStart={swiper => {
                    // 视频停止播放
                    swiper.slides.forEach(slide => {
                        const video = slide.querySelector('video') as HTMLVideoElement;
                        if (video && isVideoPlaying(video)) {
                            video.pause();
                        }
                    });
                }}
                onSlideChangeTransitionEnd={swiper => {
                    if (moduleCode) {
                        let index: number = swiper.realIndex;
                        const activeSlide = swiper.el.querySelector('.swiper-slide-active');
                        if (activeSlide) {
                            index = Number(activeSlide.getAttribute('data-t-pos')) - 1;
                        }
                        trackSlide('impr', index);
                    }
                }}
                {...swiperProps}
            />

            {/*弹窗/抽屉*/}
            <PopupDrawerModal
                visible={popupVisible}
                setVisible={setPopupVisible}
                {...popupProps}
            />
        </>
    )
}

export default React.memo(SwiperBanner);
